import * as THREE from 'three';
import { glowTexture } from '../world/textures';

export interface EmitOptions {
  count: number;
  origin: THREE.Vector3;
  /** Random offset radius around origin. */
  spread?: number;
  /** Base velocity and random velocity added per axis. */
  velocity?: THREE.Vector3;
  velocityRandom?: number;
  /** Upward bias. */
  rise?: number;
  colors: THREE.ColorRepresentation[];
  size: [number, number];
  life: [number, number];
  gravity?: number;
  drag?: number;
  /** Outward radial speed in the XZ plane. */
  radial?: number;
}

/**
 * Pooled additive particle system: one THREE.Points draw call for every particle in the scene,
 * updated on the CPU with a hard cap (quality-dependent) to keep cost bounded.
 */
export class ParticleSystem {
  readonly points: THREE.Points;
  private readonly max: number;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly size: Float32Array;
  private readonly alpha: Float32Array;
  private readonly vel: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly baseSize: Float32Array;
  private readonly grav: Float32Array;
  private readonly dragA: Float32Array;
  private cursor = 0;
  private readonly tmp = new THREE.Color();
  budgetScale = 1;

  constructor(max = 3000) {
    this.max = max;
    this.pos = new Float32Array(max * 3);
    this.col = new Float32Array(max * 3);
    this.size = new Float32Array(max);
    this.alpha = new Float32Array(max);
    this.vel = new Float32Array(max * 3);
    this.life = new Float32Array(max);
    this.maxLife = new Float32Array(max);
    this.baseSize = new Float32Array(max);
    this.grav = new Float32Array(max);
    this.dragA = new Float32Array(max);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('color', new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aSize', new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aAlpha', new THREE.BufferAttribute(this.alpha, 1).setUsage(THREE.DynamicDrawUsage));
    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uMap: { value: glowTexture() }, uScale: { value: 600 } },
      vertexShader: `
        attribute float aSize; attribute float aAlpha; varying vec3 vColor; varying float vAlpha;
        uniform float uScale;
        void main(){
          vColor = color; vAlpha = aAlpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * uScale / max(0.1, -mv.z);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform sampler2D uMap; varying vec3 vColor; varying float vAlpha;
        void main(){
          vec4 t = texture2D(uMap, gl_PointCoord);
          gl_FragColor = vec4(vColor * 1.6, t.a * vAlpha);
        }`,
      vertexColors: true,
    });
    this.points = new THREE.Points(g, mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 10;
  }

  setViewportHeight(h: number): void {
    (this.points.material as THREE.ShaderMaterial).uniforms.uScale!.value = h * 0.9;
  }

  emit(o: EmitOptions): void {
    const n = Math.max(1, Math.round(o.count * this.budgetScale));
    const v = o.velocity ?? new THREE.Vector3();
    const vr = o.velocityRandom ?? 1;
    for (let k = 0; k < n; k++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % this.max;
      const s = o.spread ?? 0;
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * s;
      this.pos[i * 3] = o.origin.x + Math.cos(a) * r;
      this.pos[i * 3 + 1] = o.origin.y + (Math.random() - 0.5) * s;
      this.pos[i * 3 + 2] = o.origin.z + Math.sin(a) * r;
      const radial = o.radial ?? 0;
      this.vel[i * 3] = v.x + (Math.random() - 0.5) * vr + Math.cos(a) * radial;
      this.vel[i * 3 + 1] = v.y + (Math.random() - 0.5) * vr + (o.rise ?? 0) * Math.random();
      this.vel[i * 3 + 2] = v.z + (Math.random() - 0.5) * vr + Math.sin(a) * radial;
      this.tmp.set(o.colors[Math.floor(Math.random() * o.colors.length)] as THREE.ColorRepresentation);
      this.col[i * 3] = this.tmp.r;
      this.col[i * 3 + 1] = this.tmp.g;
      this.col[i * 3 + 2] = this.tmp.b;
      const life = o.life[0] + Math.random() * (o.life[1] - o.life[0]);
      this.life[i] = life;
      this.maxLife[i] = life;
      this.baseSize[i] = o.size[0] + Math.random() * (o.size[1] - o.size[0]);
      this.grav[i] = o.gravity ?? 0;
      this.dragA[i] = o.drag ?? 1.5;
    }
  }

  update(dt: number): void {
    for (let i = 0; i < this.max; i++) {
      if (this.life[i]! <= 0) {
        this.alpha[i] = 0;
        continue;
      }
      const l = (this.life[i] = this.life[i]! - dt);
      if (l <= 0) {
        this.alpha[i] = 0;
        this.size[i] = 0;
        continue;
      }
      const d = Math.exp(-this.dragA[i]! * dt);
      this.vel[i * 3]! *= d;
      this.vel[i * 3 + 1] = this.vel[i * 3 + 1]! * d - this.grav[i]! * dt;
      this.vel[i * 3 + 2]! *= d;
      this.pos[i * 3] = this.pos[i * 3]! + this.vel[i * 3]! * dt;
      this.pos[i * 3 + 1] = this.pos[i * 3 + 1]! + this.vel[i * 3 + 1]! * dt;
      this.pos[i * 3 + 2] = this.pos[i * 3 + 2]! + this.vel[i * 3 + 2]! * dt;
      const u = l / this.maxLife[i]!;
      this.alpha[i] = Math.min(1, u * 2.2) * Math.min(1, (1 - u) * 8 + 0.2);
      this.size[i] = this.baseSize[i]! * (0.4 + 0.6 * u);
    }
    const g = this.points.geometry;
    g.getAttribute('position').needsUpdate = true;
    g.getAttribute('color').needsUpdate = true;
    g.getAttribute('aSize').needsUpdate = true;
    g.getAttribute('aAlpha').needsUpdate = true;
  }

  dispose(): void {
    this.points.geometry.dispose();
    (this.points.material as THREE.Material).dispose();
  }
}
